import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Divider, Drawer, Stack, TextField } from '@mui/material'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import TextFieldController from '@/components/inputs/TextFieldController'
import { ConfigField } from '@/features/controller/configuration/components/drawers/ConfigField'
import { DrawerActions } from '@/features/controller/configuration/components/drawers/DrawerActions'
import { DrawerDescription } from '@/features/controller/configuration/components/drawers/DrawerDescription'
import { DrawerTitle } from '@/features/controller/configuration/components/drawers/DrawerTitle'
import { IntegerConfigField } from '@/features/controller/configuration/components/drawers/IntegerConfigField'
import { SerialPortConfigFields } from '@/features/controller/configuration/components/drawers/SerialPortConfigFields'
import type { NamedModbusSerialConnectionNode } from '@/features/controller/configuration/models/controllerConfigTree'
import { NAMED_MODBUS_CLIENT_DEFAULTS } from '@/features/controller/configuration/models/raspiProperties/namedModbusClients'
import { NamedModbusTransport } from '@/features/controller/configuration/models/raspiProperties/types'
import { getNamedModbusSerialSchema } from '@/features/controller/configuration/schemas'
import type { ConfigDrawerProps, NamedModbusSerialSchemaType } from '@/features/controller/configuration/types'

type NamedModbusSerialConnectionDrawerProps = ConfigDrawerProps<NamedModbusSerialConnectionNode['config']> & {
  /**
   * The name is the identity other properties reference, so it can only be chosen when the
   * connection is created. Renaming an existing one would leave those references dangling.
   */
  isNewConnection: boolean
  takenNames: string[]
}

export function NamedModbusSerialConnectionDrawer({
  config,
  isNewConnection,
  onClose,
  onConfigUpdated,
  onFinishedClosing,
  open,
  takenNames,
}: Readonly<NamedModbusSerialConnectionDrawerProps>) {
  const { t } = useTranslation()
  const configPart = config.namedClient

  const form = useForm<NamedModbusSerialSchemaType>({
    defaultValues: configPart,
    mode: 'onBlur',
    resolver: zodResolver(getNamedModbusSerialSchema(takenNames)),
  })

  const hasChanges = JSON.stringify(form.watch()) !== JSON.stringify(configPart)

  function defaultHint(unit: string, defaultValue: number) {
    return t('controllers.configuration.device_connections.named_modbus.dialog.default_hint', {
      defaultValue,
      unit,
    }).trim()
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 580 },
        },
      }}
      onClose={onClose}
      onTransitionExited={onFinishedClosing}
    >
      <FormProvider {...form}>
        <DrawerTitle
          title={t('controllers.configuration.device_connections.named_modbus_serial.dialog.title', {
            name: configPart.name,
          })}
          onClose={onClose}
        />

        <Divider />

        <DrawerDescription
          description={t('controllers.configuration.device_connections.named_modbus_serial.dialog.description')}
        />

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <Stack direction="column" padding={2} spacing={2}>
            <ConfigField
              helpTooltip={
                isNewConnection
                  ? t('controllers.configuration.device_connections.named_modbus.dialog.name_hint')
                  : t('controllers.configuration.device_connections.named_modbus.dialog.name_fixed_hint')
              }
              label={t('controllers.configuration.device_connections.named_modbus.dialog.name')}
            >
              {isNewConnection ? (
                <TextFieldController name="name" type="text" />
              ) : (
                <TextField disabled={true} fullWidth={true} value={configPart.name} />
              )}
            </ConfigField>

            <SerialPortConfigFields portConfigFieldName="portConfig" serialPortFieldName="serialPort" />

            <Divider />

            <CustomTypography variant="h3">
              {t('controllers.configuration.device_connections.named_modbus.dialog.timeouts')}
            </CustomTypography>
            <CustomTypography color="text.secondary" variant="body2">
              {t('controllers.configuration.device_connections.named_modbus_serial.dialog.timeouts_description')}
            </CustomTypography>
            <IntegerConfigField
              label={t('controllers.configuration.device_connections.named_modbus.dialog.response_timeout')}
              name="responseTimeoutMs"
              textFieldProps={{
                slotProps: { htmlInput: { min: 1, step: 100 } },
              }}
              unit={defaultHint(
                t('controllers.configuration.device_connections.named_modbus.milliseconds'),
                NAMED_MODBUS_CLIENT_DEFAULTS.responseTimeoutMs,
              )}
            />
            <IntegerConfigField
              helpTooltip={t('controllers.configuration.device_connections.named_modbus.dialog.retries_tooltip')}
              label={t('controllers.configuration.device_connections.named_modbus.dialog.retries')}
              name="retries"
              textFieldProps={{
                slotProps: { htmlInput: { min: 0 } },
              }}
              unit={defaultHint('', NAMED_MODBUS_CLIENT_DEFAULTS.retries)}
            />

            <Divider />

            <CustomTypography variant="body1">
              {t('controllers.configuration.device_connections.named_modbus.dialog.attached_meters', {
                count: config.meters.length,
              })}
            </CustomTypography>
          </Stack>
        </Box>

        <Divider />

        {/* Footer actions */}
        <DrawerActions
          hasChanges={hasChanges}
          onClose={onClose}
          onSave={(updatedConfig: NamedModbusSerialSchemaType) =>
            onConfigUpdated({
              ...config,
              namedClient: {
                ...updatedConfig,
                name: isNewConnection ? updatedConfig.name : configPart.name,
                transport: NamedModbusTransport.serial,
              },
            })
          }
        />
      </FormProvider>
    </Drawer>
  )
}
